import { tenantApi } from '@shared/api/tenantApi';
import { unwrap, unwrapList } from '@shared/api/unwrap';

export type PanicLifecycleStatus =
  | 'triggered'
  | 'dispatched'
  | 'acknowledged'
  | 'in_progress'
  | 'escalated'
  | 'resolved'
  | 'cancelled';

export interface PanicLogSummary {
  panic_log_id: string;
  emergency_type_id: string;
  emergency_type_name: string;
  triggered_by_member_id?: string | null;
  triggered_by_name?: string | null;
  status: PanicLifecycleStatus | string;
  dispatch_status?: string;
  latitude?: string | null;
  longitude?: string | null;
  acknowledged_at?: string | null;
  created_at: string;
  last_lifecycle_event_at?: string | null;
}

export interface PanicLogDispatchRecipient {
  contact_name: string;
  group_name: string | null;
  channel: string;
  phone?: string | null;
  success: boolean;
  error: string | null;
}

export interface PanicLogDispatchSummary {
  attempted: number;
  sent: number;
  failed: number;
  skipped: boolean;
  reason: string | null;
  recipients: PanicLogDispatchRecipient[];
}

export interface PanicLogMetadata {
  source?: string;
  triggered_at?: string;
  accuracy_m?: number | null;
  dispatch_summary?: PanicLogDispatchSummary;
  [key: string]: unknown;
}

export interface PanicLogStatusEntry {
  panic_log_status_history_id: string;
  previous_status: string | null;
  status: string;
  note: string | null;
  actor_user_id: string | null;
  actor_member_id: string | null;
  actor_name: string | null;
  created_at: string;
}

export interface PanicLogEvent {
  panic_log_event_id: string;
  event_type: string;
  title: string;
  message: string | null;
  note: string | null;
  metadata: Record<string, unknown> | null;
  actor_name: string | null;
  created_at: string;
}

export interface PanicLogLocation {
  panic_log_location_id: string;
  latitude: string;
  longitude: string;
  accuracy_m: number | null;
  source: string;
  captured_at: string;
  created_at: string;
}

export interface PanicLogDispatchAttempt {
  panic_log_dispatch_attempt_id: string;
  recipient_name: string | null;
  recipient_group_name: string | null;
  channel: string;
  target: string;
  success: boolean;
  error: string | null;
  provider_response: Record<string, unknown> | null;
  created_at: string;
}

export type PanicLogTimelineEntryType = 'status' | 'event' | 'location' | 'dispatch';

export interface PanicLogTimelineEntry {
  id: string;
  type: PanicLogTimelineEntryType;
  title: string;
  message: string | null;
  note: string | null;
  status: string | null;
  actor_name: string | null;
  created_at: string;
  metadata: Record<string, unknown>;
  event_type?: string;
}

export interface PanicLogDetail extends PanicLogSummary {
  metadata?: PanicLogMetadata | null;
  notification_level?: string;
  resolved_at?: string | null;
  closed_at?: string | null;
  updated_at?: string;
  status_history?: PanicLogStatusEntry[];
  events?: PanicLogEvent[];
  locations?: PanicLogLocation[];
  dispatch_attempts?: PanicLogDispatchAttempt[];
  timeline?: PanicLogTimelineEntry[];
}

const ACTIVE_STATUSES = new Set(['triggered', 'dispatched', 'acknowledged', 'in_progress', 'escalated']);

export function needsAcknowledgement(log: PanicLogSummary): boolean {
  const s = String(log.status);
  return s === 'triggered' || s === 'dispatched';
}

export function isActiveStatus(status: string): boolean {
  return ACTIVE_STATUSES.has(status);
}

export async function fetchActivePanicLogs(): Promise<PanicLogSummary[]> {
  const { data } = await tenantApi.get('/panic/dashboard', {
    params: { bucket: 'active', limit: 50, sort_by: 'created_at', sort_order: 'desc' },
  });
  const rows = unwrapList<PanicLogSummary>(data);
  return rows;
}

export async function fetchPanicLogDetail(logId: string): Promise<PanicLogDetail> {
  const { data } = await tenantApi.get(`/panic/logs/${logId}`);
  return unwrap<PanicLogDetail>(data);
}

export async function acknowledgePanicLog(logId: string, note?: string): Promise<PanicLogDetail> {
  const { data } = await tenantApi.post(`/panic/logs/${logId}/status`, {
    status: 'acknowledged',
    note: note?.trim() || undefined,
  });
  return unwrap<PanicLogDetail>(data);
}
